import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Tabs, Text } from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import Breadcrumbs from "../../../components/Breadcrumbs";

import ErrorPage from "../../../pages/Error/ErrorPage";

import { useCompensationCycleDetails } from "../../../api/hooks";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import { useCompensationAccess } from "../useCompensationAccess";

const TAB_PATHS = ["cycle-overview", "my-decisions", "budget-reviews"] as const;
type TabPath = (typeof TAB_PATHS)[number];

const CycleDetailsLayout: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const {
		isLoading: isAccessLoading,
		isLoadingMyDecisions: isLoadingMyDecisionsData,
		compensationEnabled,
		canMakeDecisions,
		canCreateCycle: hasCreateCyclePermission,
		canViewCompensation: userCanViewCompensation,
		canLoadMyDecisions,
		hasDecisionsToMake,
	} = useCompensationAccess(cycleId);
	const {
		data: cycleDetails,
		isLoading: isLoadingCycleDetails,
		isError: isErrorLoadingCycle,
	} = useCompensationCycleDetails(cycleId ?? "", { enabled: compensationEnabled === true && !!cycleId });
	const cycleStartDate = cycleDetails?.startDate ? parseISO(cycleDetails.startDate) : null;
	const cycleHasStarted = cycleStartDate ? cycleStartDate <= new Date() : true;
	const cycleStatus = cycleDetails?.status || "";
	const hasMyDecisionsTab = cycleHasStarted && cycleStatus === "active" && hasDecisionsToMake;
	const location = useLocation();
	const navigate = useNavigate();

	const lastSegment = location.pathname.split("/").pop() ?? "";
	const pathTab: TabPath | null =
		lastSegment && TAB_PATHS.includes(lastSegment as TabPath) ? (lastSegment as TabPath) : null;

	const hasCanMakeDecisionsPermission = Boolean(hasMyDecisionsTab) && canMakeDecisions;

	const activeTab: TabPath = (() => {
		if (pathTab === "my-decisions") {
			if (hasMyDecisionsTab) {
				return "my-decisions";
			}
			return hasCreateCyclePermission ? "cycle-overview" : "my-decisions";
		}
		if (pathTab === "cycle-overview" || pathTab === "budget-reviews") {
			if (!hasCreateCyclePermission) {
				return "my-decisions";
			}
			return pathTab;
		}
		if (hasCanMakeDecisionsPermission) {
			return "my-decisions";
		}
		return hasCreateCyclePermission ? "cycle-overview" : "my-decisions";
	})();

	useEffect(() => {
		if (isAccessLoading || !cycleId || compensationEnabled === false) {
			return;
		}
		if (!userCanViewCompensation) {
			return;
		}

		// If user can't make decisions but can create cycles, redirect to cycle-overview
		// without waiting for myDecisionsData (which won't load).
		if (!canLoadMyDecisions && hasCreateCyclePermission) {
			const cyclePathPrefix = `/compensation/cycles/${cycleId}`;
			const isAtRoot = location.pathname === cyclePathPrefix || location.pathname === `${cyclePathPrefix}/`;
			if (isAtRoot || lastSegment !== "cycle-overview") {
				navigate("cycle-overview", { replace: true });
			}
			return;
		}

		if (isLoadingMyDecisionsData || !cycleDetails || isLoadingCycleDetails) {
			return;
		}

		const cyclePathPrefix = `/compensation/cycles/${cycleId}`;
		const isAtRoot = location.pathname === cyclePathPrefix || location.pathname === `${cyclePathPrefix}/`;

		if (isAtRoot || lastSegment !== activeTab) {
			navigate(activeTab, { replace: true });
		}
	}, [
		activeTab,
		canLoadMyDecisions,
		compensationEnabled,
		cycleId,
		cycleDetails,
		hasCreateCyclePermission,
		isAccessLoading,
		isLoadingCycleDetails,
		isLoadingMyDecisionsData,
		lastSegment,
		location.pathname,
		navigate,
		userCanViewCompensation,
	]);

	const handleTabChange = (value: string) => {
		if (TAB_PATHS.includes(value as TabPath)) {
			navigate(value);
		}
	};

	// Fast permission checks first — don't wait for slow cycle detail fetches
	if (isAccessLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (!userCanViewCompensation) {
		return <ErrorPage code={403} />;
	}

	// Data-dependent loading — only reached after permissions are confirmed
	if (
		(canLoadMyDecisions && !cycleDetails && !isErrorLoadingCycle) ||
		isLoadingCycleDetails ||
		(cycleStatus === "" && !isErrorLoadingCycle)
	) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (isErrorLoadingCycle && !hasCreateCyclePermission && !hasCanMakeDecisionsPermission) {
		return <ErrorPage code={403} />;
	}

	const tz =
		typeof cycleDetails?.timezone === "string" && cycleDetails.timezone.length > 0 ? cycleDetails.timezone : "UTC";
	const dueDateParsed = cycleDetails?.dueDate ? parseISO(cycleDetails.dueDate) : null;
	const dueDate = dueDateParsed && isValid(dueDateParsed) ? formatInTimeZone(dueDateParsed, tz, "MMM d, yyyy") : null;

	const tabItems = [
		...(hasCreateCyclePermission
			? ([
					{
						value: "cycle-overview" as const,
						label: "Cycle Overview",
						content: null,
					},
				] as const)
			: []),
		...(hasMyDecisionsTab
			? ([
					{
						value: "my-decisions" as const,
						label: "My Decisions",
						content: null,
					},
				] as const)
			: []),
		...(hasCreateCyclePermission
			? ([
					{
						value: "budget-reviews" as const,
						label: "Budget Reviews",
						content: null,
					},
				] as const)
			: []),
	];

	return (
		<BasePageTemplate title="Compensation Cycle">
			<div>
				<Breadcrumbs
					pages={[
						{
							pageID: "compensation-cycles",
							label: "Compensation Management",
							url: "/compensation",
						},
						{
							pageID: cycleId || "",
							label: String(cycleDetails?.name ?? ""),
							url: `/compensation/cycles/${cycleId}`,
						},
					]}
					className={"breadcrumbs"}
					forceHardReload
				/>
			</div>
			<div className="flex flex-col gap-6 pt-6">
				<div>
					<Text size="heading-5" weight="semi-bold" overrideElement="h1">
						{String(cycleDetails?.name ?? "")}
					</Text>

					<Text size="paragraph-3" color="neutral-7">
						Due Date: {dueDate}
					</Text>
					{tabItems.length > 0 ? (
						<div className="mt-6">
							<Tabs
								size="large"
								value={activeTab}
								color={"neutral"}
								onValueChange={handleTabChange}
								tabs={[...tabItems]}
							/>
						</div>
					) : null}
					<Outlet />
				</div>
			</div>
		</BasePageTemplate>
	);
};

export default CycleDetailsLayout;
